import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchDecisions } from '../api/endpoints';
import type { DecisionFilter } from '../api/endpoints';
import { queryKeys } from '../api/query-keys';
import type { DecisionRecord } from '../api/schemas';
import { EmptyState } from '../components/EmptyState';
import { Field } from '../components/Field';
import { QueryState } from '../components/QueryState';
import { ReasonText } from '../components/ReasonText';
import { Section } from '../components/Section';
import { StatusPill } from '../components/StatusPill';
import { TableScroll } from '../components/TableScroll';
import { DECISION_LABEL, DECISION_TONE, cooldownStateRu, positionStateRu } from '../domain/labels';
import { reasonTextRu } from '../domain/reason-codes';
import { formatDateOnly, formatDateTime, isoDateDaysAgo, todayIsoDate } from '../lib/datetime';

/**
 * Раздел 4 — «История решений».
 *
 * One row per scheduled evaluation, including the ones that decided *not* to
 * trade — those are the majority and they are the interesting ones. The reason
 * is always the Russian sentence; the identifier appears only inside an export
 * file, where a machine reads it.
 */
export function DecisionsPage(): ReactElement {
  const [from, setFrom] = useState(() => isoDateDaysAgo(30));
  const [to, setTo] = useState(() => todayIsoDate());
  const [decision, setDecision] = useState('');

  const filter = useMemo<DecisionFilter>(
    () => ({
      ...(from === '' ? {} : { from }),
      ...(to === '' ? {} : { to }),
      ...(decision === '' ? {} : { decision }),
    }),
    [from, to, decision],
  );

  const query = useQuery({
    queryKey: queryKeys.decisions(filter),
    queryFn: ({ signal }) => fetchDecisions(filter, signal),
  });

  return (
    <>
      <div className="page-head">
        <h1>История решений</h1>
        <span className="page-head__hint">
          Каждое плановое решение движка с причиной, включая пропуски входа.
        </span>
      </div>

      <div className="stack">
        <Section title="Фильтр">
          <div className="filters">
            <Field id="decisions-from" label="Дата с">
              <input
                id="decisions-from"
                type="date"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                }}
              />
            </Field>
            <Field id="decisions-to" label="Дата по">
              <input
                id="decisions-to"
                type="date"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                }}
              />
            </Field>
            <Field id="decisions-outcome" label="Решение">
              <select
                id="decisions-outcome"
                value={decision}
                onChange={(event) => {
                  setDecision(event.target.value);
                }}
              >
                <option value="">Все решения</option>
                <option value="LONG">Только входы в лонг</option>
                <option value="SHORT">Только входы в шорт</option>
                <option value="SKIP">Только пропуски</option>
              </select>
            </Field>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setFrom(isoDateDaysAgo(30));
                setTo(todayIsoDate());
                setDecision('');
              }}
            >
              Сбросить фильтр
            </button>
          </div>
        </Section>

        <Section
          title="Решения"
          hint="Причина показана текстом. Экспорт содержит и машинный код причины."
          actions={
            query.data === undefined ? undefined : <ExportButtons decisions={query.data.items} />
          }
        >
          <QueryState query={query} what="историю решений">
            {(data) =>
              data.items.length === 0 ? (
                <EmptyState>За выбранный период решений нет.</EmptyState>
              ) : (
                <DecisionsTable decisions={data.items} />
              )
            }
          </QueryState>
        </Section>
      </div>
    </>
  );
}

function DecisionsTable({ decisions }: { decisions: readonly DecisionRecord[] }): ReactElement {
  return (
    <TableScroll label="Таблица решений, прокручивается по горизонтали">
      <table className="data-table">
        <caption>Решения за выбранный период</caption>
        <thead>
          <tr>
            <th scope="col">Торговая дата</th>
            <th scope="col">Оценено</th>
            <th scope="col">Решение</th>
            <th scope="col" className="wrap">
              Причина
            </th>
            <th scope="col" className="num">
              RSI
            </th>
            <th scope="col" className="num">
              Сглаженный RSI
            </th>
            <th scope="col">Таймфрейм</th>
            <th scope="col" className="num">
              Импульс
            </th>
            <th scope="col">Пауза</th>
            <th scope="col">Позиция</th>
          </tr>
        </thead>
        <tbody>
          {decisions.map((record) => (
            <tr key={record.decisionId}>
              <td>{formatDateOnly(record.localTradingDate)}</td>
              <td>{formatDateTime(record.evaluatedAt)}</td>
              <td>
                <StatusPill tone={DECISION_TONE[record.decision]} srPrefix="Решение">
                  {DECISION_LABEL[record.decision]}
                </StatusPill>
              </td>
              <td className="wrap">
                <ReasonText code={record.reasonCode} />
              </td>
              <td className="num">{record.rsi ?? '—'}</td>
              <td className="num">{record.smoothedRsi ?? '—'}</td>
              <td>{record.timeframe}</td>
              <td className="num">{record.impulseValue ?? '—'}</td>
              <td>{cooldownStateRu(record.cooldownState)}</td>
              <td>{positionStateRu(record.positionState)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

const CSV_COLUMNS = [
  'decisionId',
  'localTradingDate',
  'evaluatedAt',
  'decision',
  'reasonCode',
  'reasonTextRu',
  'rsi',
  'smoothedRsi',
  'timeframe',
  'impulseValue',
  'cooldownState',
  'positionState',
  'mode',
  'venue',
  'symbol',
  'strategyVersion',
] as const;

function csvCell(value: string | null): string {
  const text = value ?? '';
  return `"${text.replaceAll('"', '""')}"`;
}

export function decisionsToCsv(decisions: readonly DecisionRecord[]): string {
  const header = CSV_COLUMNS.join(',');
  const rows = decisions.map((record) =>
    [
      record.decisionId,
      record.localTradingDate,
      record.evaluatedAt,
      record.decision,
      record.reasonCode,
      reasonTextRu(record.reasonCode),
      record.rsi,
      record.smoothedRsi,
      record.timeframe,
      record.impulseValue,
      record.cooldownState,
      record.positionState,
      record.mode,
      record.venue,
      record.symbol,
      record.strategyVersion,
    ]
      .map(csvCell)
      .join(','),
  );
  return [header, ...rows].join('\r\n');
}

function download(filename: string, contents: string, mime: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  // A BOM keeps Cyrillic readable when the CSV is opened in Excel.
  const blob = new Blob([`\uFEFF${contents}`], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function ExportButtons({ decisions }: { decisions: readonly DecisionRecord[] }): ReactElement {
  return (
    <div className="btn-row">
      <button
        type="button"
        className="btn"
        onClick={() => {
          download('decisions.csv', decisionsToCsv(decisions), 'text/csv;charset=utf-8');
        }}
      >
        Экспорт CSV
      </button>
      <button
        type="button"
        className="btn"
        onClick={() => {
          download(
            'decisions.json',
            JSON.stringify(decisions, null, 2),
            'application/json;charset=utf-8',
          );
        }}
      >
        Экспорт JSON
      </button>
    </div>
  );
}
